import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, HandHelping, Clock, Calendar, BarChart3, Cake, UserCheck, TrendingUp, Crown, UserPlus, AlertCircle, TrendingDown, UserX, Music } from 'lucide-react';
import { fmtDate } from '@/lib/helpers';

export default function DashboardTab({ overview, pessoas, pessoasStats, eventsEngagement, events, frequencyReport, tiposEvento, engajamentoDisplay = 'niveis' }) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // Use pessoasStats for fila/freq data, pessoas for demographics (batizado, created_at)
  const statsData = pessoasStats || [];
  const fullPessoas = pessoas || [];

  // Aniversariantes do mês (parse manual to avoid timezone issues)
  const aniversariantes = statsData.filter(p => {
    if (!p.data_nascimento) return false;
    const parts = p.data_nascimento.split('-');
    const month = parseInt(parts[1], 10) - 1; // 0-indexed
    return month === currentMonth;
  }).sort((a, b) => {
    const dayA = parseInt(a.data_nascimento.split('-')[2], 10);
    const dayB = parseInt(b.data_nascimento.split('-')[2], 10);
    return dayA - dayB;
  });

  // Eventos fechados/abertos
  const allEvents = events || [];
  const eventosFechados = allEvents.filter(e => e.status === 'fechado');
  const eventosAbertos = allEvents.filter(e => e.status === 'aberto').sort((a, b) => new Date(a.date || a.datetime || '') - new Date(b.date || b.datetime || ''));
  const tipoMap = {};
  (tiposEvento || []).forEach(t => { tipoMap[t.tipo_evento_id] = t.nome; });

  // Engajamento médio (from eventsEngagement)
  const engagementData = eventsEngagement || [];
  const avgEngagement = engagementData.length > 0
    ? Math.round(engagementData.reduce((s, e) => s + (e.frequencia_pct || 0), 0) / engagementData.length)
    : 0;

  // Últimos 12 eventos para gráfico de presença (sorted by date asc for chart)
  const last12Events = [...engagementData]
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .slice(-12);
  const maxPresEvent = Math.max(...last12Events.map(e => e.presentes || 0), 1);

  // Crescimento por mês (últimos 6 meses) - use fullPessoas which has created_at
  const monthlyGrowth = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(currentYear, currentMonth - i, 1);
    const m = d.getMonth();
    const y = d.getFullYear();
    const count = fullPessoas.filter(p => {
      if (!p.created_at) return false;
      const c = new Date(p.created_at);
      return c.getMonth() === m && c.getFullYear() === y;
    }).length;
    const label = d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    monthlyGrowth.push({ label, count });
  }
  const maxGrowth = Math.max(...monthlyGrowth.map(m => m.count), 1);

  // Distribuição por gênero
  const masculino = statsData.filter(p => p.genero === 'masculino').length;
  const feminino = statsData.filter(p => p.genero === 'feminino').length;
  const totalGenero = masculino + feminino || 1;

  // Top 10 instrumentos
  const instrumentCount = {};
  fullPessoas.forEach(p => {
    (p.instrumentos || []).forEach(inst => {
      if (inst) instrumentCount[inst] = (instrumentCount[inst] || 0) + 1;
    });
  });
  const topInstrumentos = Object.entries(instrumentCount)
    .map(([nome, count]) => ({ nome, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  const maxInstrumento = Math.max(...topInstrumentos.map(i => i.count), 1);

  // Helper to get numeric age
  const getAge = (dob) => {
    if (!dob) return null;
    const [y, m, d] = dob.split('-').map(Number);
    const today = new Date();
    let age = today.getFullYear() - y;
    if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
    return age;
  };

  // Distribuição por faixa etária
  const faixaDefs = [
    { label: '0-5', min: 0, max: 5 },
    { label: '6-11', min: 6, max: 11 },
    { label: '12-15', min: 12, max: 15 },
    { label: '16-18', min: 16, max: 18 },
    { label: '19-24', min: 19, max: 24 },
    { label: '25+', min: 25, max: 200 },
  ];
  const FAIXA_COLORS_M = ['#3B82F6', '#2563EB', '#1D4ED8', '#1E40AF', '#1E3A8A', '#172554'];
  const FAIXA_COLORS_F = ['#EC4899', '#DB2777', '#BE185D', '#9D174D', '#831843', '#500724'];
  const faixaByGenero = (genero) => faixaDefs.map(f => {
    const count = statsData.filter(p => {
      if (!p.data_nascimento || p.genero !== genero) return false;
      const age = getAge(p.data_nascimento);
      return age !== null && age >= f.min && age <= f.max;
    }).length;
    return { ...f, count };
  });
  const faixaMeninos = faixaByGenero('masculino');
  const faixaMeninas = faixaByGenero('feminino');
  const maxFaixa = Math.max(...[...faixaMeninos, ...faixaMeninas].map(f => f.count), 1);

  // Batismo (somente pessoas com 12 anos ou mais)
  const pessoasBatismoElegiveis = fullPessoas.filter(p => {
    const age = getAge(p.data_nascimento);
    return age !== null && age >= 12;
  });
  const batizados = pessoasBatismoElegiveis.filter(p => p.batizado === true).length;
  const naoBatizados = pessoasBatismoElegiveis.filter(p => !p.batizado).length;
  const totalBatismo = batizados + naoBatizados || 1;

  // Últimos cadastros (from fullPessoas which has .created_at)
  const ultimosCadastros = [...fullPessoas]
    .filter(p => p.created_at)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  // Top 10 presença (from frequencyReport: pessoa_nome, percentage)
  const top10 = [...(frequencyReport || [])]
    .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
    .slice(0, 10);

  // Bottom 10 menos engajados (exclui 0% que vão para "Nunca Compareceram")
  const bottom10 = [...(frequencyReport || [])]
    .filter(p => (p.percentage || 0) > 0)
    .sort((a, b) => (a.percentage || 0) - (b.percentage || 0))
    .slice(0, 10);

  // Nunca compareceram (0% de presença)
  const neverAttended = [...(frequencyReport || [])]
    .filter(p => (p.percentage || 0) === 0)
    .sort((a, b) => (a.pessoa_nome || '').localeCompare(b.pessoa_nome || ''));

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {/* Linha 1 - 6 Cards de Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dashboard-summary-cards">
        <SummaryCard label="Pessoas" value={overview?.total_pessoas || 0} icon={Users} color="text-orange-500" bgColor="bg-orange-50" testId="dash-pessoas" />
        <SummaryCard label="Auxiliares" value={overview?.total_auxiliares || 0} icon={HandHelping} color="text-blue-600" bgColor="bg-blue-50" testId="dash-auxiliares" />
        <SummaryCard label="Pendentes" value={overview?.total_pendentes || 0} icon={Clock} color="text-red-600" bgColor="bg-red-50" testId="dash-pendentes" alert={overview?.total_pendentes > 0} />
        <SummaryCard label="Eventos Realizados" value={eventosFechados.length} icon={Calendar} color="text-green-600" bgColor="bg-green-50" testId="dash-eventos" />
        <SummaryCard label="Engajamento" value={`${avgEngagement}%`} icon={TrendingUp} color="text-indigo-600" bgColor="bg-indigo-50" testId="dash-engajamento" />
        <SummaryCard label="Aniversariantes" value={aniversariantes.length} icon={Cake} color="text-pink-600" bgColor="bg-pink-50" testId="dash-aniversariantes" subtitle="este mês" />
      </div>

      {/* Linha 2 - Gráficos Principais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Presença nos últimos 12 eventos */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center"><BarChart3 className="w-4 h-4 text-indigo-600" /></div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Presença por Evento</h3>
                <p className="text-xs text-slate-500">Últimos {last12Events.length} eventos</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {last12Events.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="flex items-end gap-2" style={{ minWidth: last12Events.length > 6 ? `${last12Events.length * 65}px` : 'auto' }} data-testid="dash-chart-attendance">
                  {last12Events.map((ev, i) => {
                    const pct = ev.frequencia_pct || 0;
                    const barH = Math.max(((ev.presentes || 0) / maxPresEvent) * 100, 6);
                    return (
                      <div key={ev.event_id || i} className="flex-1 min-w-[50px] max-w-[80px] text-center">
                        <div className="h-28 flex items-end justify-center mb-1">
                          <div className="w-8 sm:w-10 rounded-t-lg transition-all relative group cursor-default" style={{ height: `${barH}%`, background: pct >= 70 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#EF4444' }}>
                            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              {ev.presentes}/{ev.total_pessoas} ({pct}%)
                            </div>
                          </div>
                        </div>
                        <p className="text-[9px] sm:text-[10px] text-slate-500 truncate">{ev.name}</p>
                        <p className="text-xs font-bold text-slate-900">{pct}%</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <EmptyState text="Nenhum evento com presença registada" />
            )}
          </CardContent>
        </Card>

        {/* Crescimento de membros por mês */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center"><UserPlus className="w-4 h-4 text-green-600" /></div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Novos Cadastros</h3>
                <p className="text-xs text-slate-500">Últimos 6 meses</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-3 justify-around" data-testid="dash-chart-growth">
              {monthlyGrowth.map((m, i) => {
                const barH = Math.max((m.count / maxGrowth) * 100, 6);
                return (
                  <div key={i} className="flex-1 max-w-[80px] text-center">
                    <div className="h-28 flex items-end justify-center mb-1">
                      <div className="w-10 sm:w-12 rounded-t-lg transition-all relative group cursor-default" style={{ height: `${barH}%`, background: '#10B981' }}>
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          {m.count} cadastros
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 capitalize">{m.label}</p>
                    <p className="text-sm font-bold text-slate-900">{m.count}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Linha 3 - Distribuições */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Distribuição por Gênero */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center"><Users className="w-4 h-4 text-pink-600" /></div>
              <h3 className="text-sm font-semibold text-slate-900">Gênero</h3>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4" data-testid="dash-gender-dist">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-slate-600">Masculino</span>
                  <span className="text-xs font-bold text-blue-600">{masculino} ({Math.round((masculino / totalGenero) * 100)}%)</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${(masculino / totalGenero) * 100}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-slate-600">Feminino</span>
                  <span className="text-xs font-bold text-pink-600">{feminino} ({Math.round((feminino / totalGenero) * 100)}%)</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-pink-500 transition-all" style={{ width: `${(feminino / totalGenero) * 100}%` }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Distribuição por Faixa Etária */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><Cake className="w-4 h-4 text-amber-600" /></div>
              <h3 className="text-sm font-semibold text-slate-900">Faixa Etária</h3>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4" data-testid="dash-age-dist">
              <div>
                <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-2">Meninos</p>
                <div className="space-y-2">
                  {faixaMeninos.map((f, i) => (
                    <div key={f.label} className="flex items-center gap-2">
                      <p className="text-xs font-medium text-slate-700 w-12">{f.label}</p>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(f.count / maxFaixa) * 100}%`, background: FAIXA_COLORS_M[i] }} />
                      </div>
                      <p className="text-xs font-bold text-slate-900 w-6 text-right">{f.count}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-2">
                <p className="text-[10px] font-semibold text-pink-500 uppercase tracking-wider mb-2">Meninas</p>
                <div className="space-y-2">
                  {faixaMeninas.map((f, i) => (
                    <div key={f.label} className="flex items-center gap-2">
                      <p className="text-xs font-medium text-slate-700 w-12">{f.label}</p>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(f.count / maxFaixa) * 100}%`, background: FAIXA_COLORS_F[i] }} />
                      </div>
                      <p className="text-xs font-bold text-slate-900 w-6 text-right">{f.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Linha 4 - Informações Operacionais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Eventos Abertos */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center"><AlertCircle className="w-4 h-4 text-orange-600" /></div>
                <h3 className="text-sm font-semibold text-slate-900">Eventos Abertos</h3>
              </div>
              <Badge className="bg-orange-100 text-orange-700 text-xs font-bold">{eventosAbertos.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {eventosAbertos.length > 0 ? (
              <div className="divide-y divide-slate-100" data-testid="dash-open-events">
                {eventosAbertos.slice(0, 6).map((ev) => {
                  const evDate = ev.date || ev.datetime || '';
                  const daysOpen = evDate ? Math.max(0, Math.floor((now - new Date(evDate)) / (1000 * 60 * 60 * 24))) : 0;
                  return (
                    <div key={ev.event_id} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-slate-900 truncate">{ev.name}</p>
                            {ev.tipo_evento_id && tipoMap[ev.tipo_evento_id] && (
                              <Badge variant="outline" className="text-[10px] flex-shrink-0">{tipoMap[ev.tipo_evento_id]}</Badge>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-500">{fmtDate(evDate)}</p>
                        </div>
                      </div>
                      {daysOpen > 0 && <Badge className="bg-red-100 text-red-700 text-[10px] flex-shrink-0">{daysOpen}d aberto</Badge>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState text="Nenhum evento aberto" />
            )}
          </CardContent>
        </Card>

        {/* Últimos Cadastros */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center"><UserPlus className="w-4 h-4 text-green-600" /></div>
                <h3 className="text-sm font-semibold text-slate-900">Últimos Cadastros</h3>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ultimosCadastros.length > 0 ? (
              <div className="divide-y divide-slate-100" data-testid="dash-recent-registrations">
                {ultimosCadastros.map((p, i) => (
                  <div key={p.pessoa_id || i} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <Users className="w-3 h-3 text-slate-500" />
                      </div>
                      <p className="text-sm font-medium text-slate-900 truncate">{p.nome_completo}</p>
                    </div>
                    <p className="text-[10px] text-slate-500 flex-shrink-0">{p.created_at ? fmtDate(p.created_at.split('T')[0]) : ''}</p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="Nenhum cadastro recente" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Linha 5 - Presença e Engajamento (only in percentual mode) */}
      {engajamentoDisplay === 'percentual' && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {/* Top 10 Presença */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><Crown className="w-4 h-4 text-amber-600" /></div>
              <h3 className="text-sm font-semibold text-slate-900">Top 10 Presença</h3>
            </div>
          </CardHeader>
          <CardContent>
            {top10.length > 0 ? (
              <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="dash-top-attendance">
                {top10.map((p, i) => {
                  const pct = p.percentage || 0;
                  const color = pct >= 70 ? '#10B981' : pct >= 40 ? '#F59E0B' : '#EF4444';
                  return (
                    <div key={p.pessoa_id || i} className="flex items-center gap-2 py-2 first:pt-0 last:pb-0">
                      <span className="text-[10px] font-bold text-slate-400 w-4 text-center">{i + 1}</span>
                      <p className="text-sm text-slate-900 truncate flex-1">{p.pessoa_nome}</p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
                        </div>
                        <span className="text-xs font-bold w-10 text-right" style={{ color }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState text="Nenhum dado de presença" />
            )}
          </CardContent>
        </Card>

        {/* Menos Engajados - only show in percentual mode */}
        {engajamentoDisplay === 'percentual' && (
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center"><TrendingDown className="w-4 h-4 text-red-600" /></div>
              <h3 className="text-sm font-semibold text-slate-900">Menos Engajados</h3>
            </div>
          </CardHeader>
          <CardContent>
            {bottom10.length > 0 ? (
              <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="dash-bottom-attendance">
                {bottom10.map((p, i) => {
                  const pct = p.percentage || 0;
                  const color = pct >= 40 ? '#F59E0B' : '#EF4444';
                  return (
                    <div key={p.pessoa_id || i} className="flex items-center gap-2 py-2 first:pt-0 last:pb-0">
                      <span className="text-[10px] font-bold text-slate-400 w-4 text-center">{i + 1}</span>
                      <p className="text-sm text-slate-900 truncate flex-1">{p.pessoa_nome}</p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
                        </div>
                        <span className="text-xs font-bold w-10 text-right" style={{ color }}>{pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState text="Nenhum dado de presença" />
            )}
          </CardContent>
        </Card>
        )}

        {/* Nunca Compareceram - only show in percentual mode */}
        {engajamentoDisplay === 'percentual' && (
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><UserX className="w-4 h-4 text-slate-600" /></div>
                <h3 className="text-sm font-semibold text-slate-900">Nunca Compareceram</h3>
              </div>
              <Badge className="bg-slate-100 text-slate-700 text-xs font-bold">{neverAttended.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {neverAttended.length > 0 ? (
              <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="dash-never-attended">
                {neverAttended.map((p, i) => (
                  <div key={p.pessoa_id || i} className="flex items-center justify-between py-2 first:pt-0 last:pb-0">
                    <p className="text-sm text-slate-900 truncate">{p.pessoa_nome}</p>
                    <span className="text-xs text-red-500 font-medium flex-shrink-0">0%</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="Todos os membros já compareceram" />
            )}
          </CardContent>
        </Card>
        )}
      </div>
      )}

      {/* Linha 6 - Batismo, Instrumentos e Aniversariantes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {/* Batismo */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center"><UserCheck className="w-4 h-4 text-cyan-600" /></div>
              <h3 className="text-sm font-semibold text-slate-900">Batismo</h3>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4" data-testid="dash-baptism">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-slate-600">Batizados</span>
                  <span className="text-xs font-bold text-cyan-600">{batizados} ({Math.round((batizados / totalBatismo) * 100)}%)</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${(batizados / totalBatismo) * 100}%` }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-slate-600">Não Batizados</span>
                  <span className="text-xs font-bold text-red-500">{naoBatizados} ({Math.round((naoBatizados / totalBatismo) * 100)}%)</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-red-400 transition-all" style={{ width: `${(naoBatizados / totalBatismo) * 100}%` }} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top 10 Instrumentos */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center"><Music className="w-4 h-4 text-purple-600" /></div>
              <h3 className="text-sm font-semibold text-slate-900">Top 10 Instrumentos</h3>
            </div>
          </CardHeader>
          <CardContent>
            {topInstrumentos.length > 0 ? (
              <div className="space-y-3 max-h-48 overflow-y-auto" data-testid="dash-top-instruments">
                {topInstrumentos.map((inst, i) => (
                  <div key={inst.nome} className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-slate-400 w-4 text-center">{i + 1}</span>
                    <p className="text-xs font-medium text-slate-700 w-24 truncate">{inst.nome}</p>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${(inst.count / maxInstrumento) * 100}%` }} />
                    </div>
                    <p className="text-xs font-bold text-slate-900 w-6 text-right">{inst.count}</p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text="Nenhum instrumento registado" />
            )}
          </CardContent>
        </Card>

        {/* Aniversariantes do Mês */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center"><Cake className="w-4 h-4 text-pink-600" /></div>
                <h3 className="text-sm font-semibold text-slate-900">Aniversariantes</h3>
              </div>
              <Badge className="bg-pink-100 text-pink-700 text-xs font-bold">{aniversariantes.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {aniversariantes.length > 0 ? (
              <div className="divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="dash-birthdays">
                {aniversariantes.map((p, i) => {
                  const day = parseInt(p.data_nascimento.split('-')[2], 10);
                  return (
                    <div key={p.pessoa_id || i} className="flex items-center justify-between py-2 first:pt-0 last:pb-0">
                      <p className="text-sm text-slate-900 truncate">{p.nome_completo}</p>
                      <span className="text-xs text-pink-600 font-medium flex-shrink-0">dia {day}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState text="Nenhum aniversariante este mês" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, bgColor, testId, alert, subtitle }) {
  return (
    <Card className="bg-white border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between mb-1 sm:mb-2">
          <p className="text-[10px] sm:text-xs text-slate-500">{label}</p>
          <div className={`w-7 h-7 rounded-lg ${bgColor} flex items-center justify-center`}>
            <Icon className={`w-3.5 h-3.5 ${color}`} />
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <p className={`text-xl sm:text-2xl font-bold ${color}`}>{value}</p>
          {alert && <Badge className="bg-red-600 text-white text-[10px]">!</Badge>}
        </div>
        {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function EmptyState({ text }) {
  return (
    <div className="py-6 text-center">
      <p className="text-sm text-slate-400">{text}</p>
    </div>
  );
}
